import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  writeBatch,
  onSnapshot
} from 'firebase/firestore';
import { db } from '@/shared/config/firebase';

import {
  UserAffiliation,
  UserAffiliationBase,
  CreateUserAffiliationRequest,
  UpdateUserAffiliationRequest,
  generateAffiliationId
} from '@/models/userAffiliation';
import { RoleRepository } from './role-repository';
import { Role, EMPTY_PERMISSIONS } from '@/models/role';

/**
 * Repository pour gérer les affiliations des utilisateurs aux organisations
 * Gère les relations entre utilisateurs et organisations avec rôles et permissions
 */
export class UserAffiliationRepository {
  private static readonly COLLECTION_NAME = 'userAffiliations';

  /**
   * Résout le rôle et les permissions pour une affiliation
   * Fait la jointure avec la collection roles
   */
  private static async resolveRole(roleName: string): Promise<{ role: Role | null; permissions: any }> {
    try {
      const role = await RoleRepository.getByName(roleName);
      if (role) {
        return { role, permissions: role.permissions };
      }
      // Si le rôle n'existe pas dans Firestore, retourner des permissions vides
      console.error(`❌ Rôle ${roleName} non trouvé dans Firestore. Vérifiez que le rôle existe dans la collection 'roles'.`);
      return { role: null, permissions: EMPTY_PERMISSIONS };
    } catch (error) {
      console.error(`❌ Erreur lors de la résolution du rôle ${roleName}:`, error);
      return { role: null, permissions: EMPTY_PERMISSIONS };
    }
  }

  /**
   * Résout les rôles pour plusieurs affiliations en batch
   */
  private static async resolveRolesBatch(roleNames: string[]): Promise<Map<string, { role: Role | null; permissions: any }>> {
    const uniqueRoleNames = [...new Set(roleNames)];
    const roleMap = new Map<string, { role: Role | null; permissions: any }>();

    // Charger tous les rôles uniques en parallèle
    await Promise.all(
      uniqueRoleNames.map(async (roleName) => {
        const resolved = await this.resolveRole(roleName);
        roleMap.set(roleName, resolved);
      })
    );

    return roleMap;
  }

  /**
   * Récupère toutes les organisations d'un customer
   * @deprecated Utiliser getUserOrganizationsByUid de préférence
   */
  static async getUserOrganizations(customerId: string, firebaseUid?: string): Promise<UserAffiliation[]> {
    try {
      console.log(`🔍 Récupération des organisations pour le customer: ${customerId}${firebaseUid ? ` (UID: ${firebaseUid})` : ''}`);

      // Essayer d'abord avec le customerId
      let q = query(
        collection(db, this.COLLECTION_NAME),
        where('customerId', '==', customerId),
        where('status', '==', 'active')
      );

      let querySnapshot = await getDocs(q);

      // Si aucune affiliation trouvée avec customerId et qu'on a un firebaseUid, essayer avec firebaseUid
      if (querySnapshot.empty && firebaseUid) {
        console.log(`⚠️ Aucune orga trouvée avec customerId=${customerId}, essai avec firebaseUid=${firebaseUid}`);
        q = query(
          collection(db, this.COLLECTION_NAME),
          where('customerId', '==', firebaseUid),
          where('status', '==', 'active')
        );
        querySnapshot = await getDocs(q);
      }

      const affiliationsBase: UserAffiliationBase[] = [];
      const roleNames: string[] = [];

      // Première passe : parser les données de base et collecter les noms de rôles
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        try {
          // Migration: supporter à la fois l'ancien format (role) et le nouveau (roleName)
          const roleName = data.roleName || data.role || 'member';

          const affiliationBase: UserAffiliationBase = {
            id: doc.id,
            customerId: data.customerId,
            email: data.email,
            organizationId: data.organizationId,
            roleName: roleName,
            status: data.status || 'active',
            joinedAt: data.joinedAt?.toDate ? data.joinedAt.toDate() : new Date(data.joinedAt || Date.now()),
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || Date.now()),
            invitedBy: data.invitedBy,
            invitedAt: data.invitedAt?.toDate ? data.invitedAt.toDate() : (data.invitedAt ? new Date(data.invitedAt) : undefined),
            lastActivityAt: data.lastActivityAt?.toDate ? data.lastActivityAt.toDate() : (data.lastActivityAt ? new Date(data.lastActivityAt) : undefined),
          };

          affiliationsBase.push(affiliationBase);
          roleNames.push(roleName);
        } catch (error) {
          console.warn(`⚠️ Affiliation ${doc.id} invalide, ignorée:`, error);
        }
      });

      // Si aucune affiliation trouvée, retourner vide
      if (affiliationsBase.length === 0) {
        console.log(`✅ 0 organisations trouvées`);
        return [];
      }

      // Résoudre tous les rôles en batch
      const roleMap = await this.resolveRolesBatch(roleNames);

      // Deuxième passe : enrichir les affiliations avec les rôles résolus
      const affiliations: UserAffiliation[] = affiliationsBase.map((affiliationBase) => {
        const resolved = roleMap.get(affiliationBase.roleName) || { role: null, permissions: EMPTY_PERMISSIONS };

        const affiliation: UserAffiliation = {
          ...affiliationBase,
          roleData: resolved.role || undefined,
          permissions: resolved.permissions,
          // Alias pour compatibilité avec l'ancien code
          role: affiliationBase.roleName as 'owner' | 'admin' | 'member' | 'viewer',
        };

        return affiliation;
      });

      // Trier côté client par joinedAt (plus récent en premier)
      affiliations.sort((a, b) => {
        const dateA = a.joinedAt.getTime();
        const dateB = b.joinedAt.getTime();
        return dateB - dateA; // Descendant
      });

      console.log(`✅ ${affiliations.length} organisations trouvées`);
      return affiliations;

    } catch (error) {
      console.error(`❌ Erreur lors de la récupération des organisations:`, error);
      throw error;
    }
  }

  /**
   * Récupère toutes les organisations d'un utilisateur par son Firebase UID
   */
  static async getUserOrganizationsByUid(firebaseUid: string): Promise<UserAffiliation[]> {
    return this.getUserOrganizations(firebaseUid, firebaseUid);
  }

  /**
   * S'abonne aux changements en temps réel des organisations d'un customer
   */
  static subscribeByCustomerId(
    customerId: string,
    onUpdate: (affiliations: UserAffiliation[]) => void,
    onError?: (error: Error) => void
  ): () => void {
    try {
      console.log(`🔍 Abonnement aux organisations pour le customer: ${customerId}`);

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('customerId', '==', customerId),
        where('status', '==', 'active')
      );

      const unsubscribe = onSnapshot(
        q,
        async (querySnapshot) => {
          const affiliationsBase: UserAffiliationBase[] = [];
          const roleNames: string[] = [];

          querySnapshot.forEach((doc) => {
            const data = doc.data();
            try {
              const roleName = data.roleName || data.role || 'member';

              const affiliationBase: UserAffiliationBase = {
                id: doc.id,
                customerId: data.customerId,
                email: data.email,
                organizationId: data.organizationId,
                roleName: roleName,
                status: data.status || 'active',
                emailNotifications: data.emailNotifications,
                joinedAt: data.joinedAt?.toDate ? data.joinedAt.toDate() : new Date(data.joinedAt || Date.now()),
                updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || Date.now()),
                invitedBy: data.invitedBy,
                invitedAt: data.invitedAt?.toDate ? data.invitedAt.toDate() : (data.invitedAt ? new Date(data.invitedAt) : undefined),
                lastActivityAt: data.lastActivityAt?.toDate ? data.lastActivityAt.toDate() : (data.lastActivityAt ? new Date(data.lastActivityAt) : undefined),
              };

              affiliationsBase.push(affiliationBase);
              roleNames.push(roleName);
            } catch (error) {
              console.warn(`⚠️ Affiliation ${doc.id} invalide, ignorée:`, error);
            }
          });

          // Résoudre tous les rôles en batch
          const roleMap = await this.resolveRolesBatch(roleNames);

          // Enrichir les affiliations avec les rôles résolus
          const affiliations: UserAffiliation[] = affiliationsBase.map((affiliationBase) => {
            const resolved = roleMap.get(affiliationBase.roleName) || { role: null, permissions: EMPTY_PERMISSIONS };

            return {
              ...affiliationBase,
              roleData: resolved.role || undefined,
              permissions: resolved.permissions,
              role: affiliationBase.roleName as 'owner' | 'admin' | 'member' | 'viewer',
            };
          });

          // Trier par joinedAt
          affiliations.sort((a, b) => b.joinedAt.getTime() - a.joinedAt.getTime());

          onUpdate(affiliations);
        },
        (error) => {
          console.error(`❌ Erreur lors de l'écoute des organisations pour ${customerId}:`, error);
          if (onError) {
            onError(error as Error);
          }
        }
      );

      return unsubscribe;
    } catch (error) {
      console.error(`❌ Erreur lors de la configuration de l'abonnement:`, error);
      if (onError) {
        onError(error as Error);
      }
      return () => { };
    }
  }

  /**
   * Récupère tous les utilisateurs d'une organisation
   */
  static async getOrganizationUsers(organizationId: string): Promise<UserAffiliation[]> {
    try {
      console.log(`🔍 Récupération des utilisateurs pour l'organisation: ${organizationId}`);

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('organizationId', '==', organizationId),
        where('status', '==', 'active'),
        orderBy('joinedAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const affiliationsBase: UserAffiliationBase[] = [];
      const roleNames: string[] = [];

      // Première passe : parser les données de base
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        try {
          // Migration: supporter à la fois l'ancien format (role) et le nouveau (roleName)
          const roleName = data.roleName || data.role || 'member';

          const affiliationBase: UserAffiliationBase = {
            id: doc.id,
            customerId: data.customerId,
            email: data.email,
            organizationId: data.organizationId,
            roleName: roleName,
            status: data.status || 'active',
            joinedAt: data.joinedAt?.toDate ? data.joinedAt.toDate() : new Date(data.joinedAt || Date.now()),
            updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || Date.now()),
            invitedBy: data.invitedBy,
            invitedAt: data.invitedAt?.toDate ? data.invitedAt.toDate() : (data.invitedAt ? new Date(data.invitedAt) : undefined),
            lastActivityAt: data.lastActivityAt?.toDate ? data.lastActivityAt.toDate() : (data.lastActivityAt ? new Date(data.lastActivityAt) : undefined),
          };

          affiliationsBase.push(affiliationBase);
          roleNames.push(roleName);
        } catch (error) {
          console.warn(`⚠️ Affiliation ${doc.id} invalide, ignorée:`, error);
        }
      });

      // Résoudre tous les rôles en batch
      const roleMap = await this.resolveRolesBatch(roleNames);

      // Deuxième passe : enrichir les affiliations avec les rôles résolus
      const affiliations: UserAffiliation[] = affiliationsBase.map((affiliationBase) => {
        const resolved = roleMap.get(affiliationBase.roleName) || { role: null, permissions: EMPTY_PERMISSIONS };

        return {
          ...affiliationBase,
          roleData: resolved.role || undefined,
          permissions: resolved.permissions,
          // Alias pour compatibilité avec l'ancien code
          role: affiliationBase.roleName as 'owner' | 'admin' | 'member' | 'viewer',
        };
      });

      console.log(`✅ ${affiliations.length} utilisateurs trouvés pour l'organisation ${organizationId}`);
      return affiliations;


    } catch (error) {
      console.error(`❌ Erreur lors de la récupération des utilisateurs pour ${organizationId}:`, error);
      throw error;
    }
  }

  /**
   * Récupère une affiliation par firebaseUid et organizationId
   * Utilise l'ID structuré directement (plus performant, sans query)
   * 
   * @param firebaseUid - Firebase Auth UID de l'utilisateur
   * @param organizationId - ID de l'organisation
   */
  static async getAffiliationByFirebaseUid(firebaseUid: string, organizationId: string): Promise<UserAffiliation | null> {
    try {
      console.log(`🔍 Récupération de l'affiliation par UID: ${firebaseUid} -> ${organizationId}`);

      // Utiliser l'ID structuré pour un accès direct (sans query)
      const affiliationId = generateAffiliationId(firebaseUid, organizationId);
      const docRef = doc(db, this.COLLECTION_NAME, affiliationId);
      const docSnapshot = await getDoc(docRef);

      if (!docSnapshot.exists()) {
        console.log(`❌ Aucune affiliation trouvée: ${affiliationId}`);
        return null;
      }

      const data = docSnapshot.data();
      const roleName = data.roleName || data.role || 'member';

      const affiliationBase: UserAffiliationBase = {
        id: affiliationId,
        customerId: data.customerId,
        email: data.email,
        organizationId: data.organizationId,
        roleName: roleName,
        status: data.status || 'active',
        joinedAt: data.joinedAt?.toDate ? data.joinedAt.toDate() : new Date(data.joinedAt || Date.now()),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || Date.now()),
        invitedBy: data.invitedBy,
        invitedAt: data.invitedAt?.toDate ? data.invitedAt.toDate() : (data.invitedAt ? new Date(data.invitedAt) : undefined),
        lastActivityAt: data.lastActivityAt?.toDate ? data.lastActivityAt.toDate() : (data.lastActivityAt ? new Date(data.lastActivityAt) : undefined),
      };

      const resolved = await this.resolveRole(roleName);

      const affiliation: UserAffiliation = {
        ...affiliationBase,
        roleData: resolved.role || undefined,
        permissions: resolved.permissions,
        role: roleName as 'owner' | 'admin' | 'member' | 'viewer',
      };

      console.log(`✅ Affiliation trouvée: ${affiliationId} (${affiliation.roleName})`);
      return affiliation;

    } catch (error) {
      console.error(`❌ Erreur lors de la récupération de l'affiliation par UID:`, error);
      throw error;
    }
  }

  /**
   * Récupère une affiliation spécifique par customerId et organizationId
   * @deprecated Utiliser getAffiliationByFirebaseUid pour les utilisateurs authentifiés
   */
  static async getAffiliation(customerId: string, organizationId: string): Promise<UserAffiliation | null> {
    try {
      console.log(`🔍 Récupération de l'affiliation: ${customerId} -> ${organizationId}`);

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('customerId', '==', customerId),
        where('organizationId', '==', organizationId)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.log(`❌ Aucune affiliation trouvée: ${customerId} -> ${organizationId}`);
        return null;
      }

      const docSnap = querySnapshot.docs[0];
      const data = docSnap.data();

      // Migration: supporter à la fois l'ancien format (role) et le nouveau (roleName)
      const roleName = data.roleName || data.role || 'member';

      const affiliationBase: UserAffiliationBase = {
        id: docSnap.id,
        customerId: data.customerId,
        email: data.email,
        organizationId: data.organizationId,
        roleName: roleName,
        status: data.status || 'active',
        joinedAt: data.joinedAt?.toDate ? data.joinedAt.toDate() : new Date(data.joinedAt || Date.now()),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || Date.now()),
        invitedBy: data.invitedBy,
        invitedAt: data.invitedAt?.toDate ? data.invitedAt.toDate() : (data.invitedAt ? new Date(data.invitedAt) : undefined),
        lastActivityAt: data.lastActivityAt?.toDate ? data.lastActivityAt.toDate() : (data.lastActivityAt ? new Date(data.lastActivityAt) : undefined),
      };

      // Résoudre le rôle
      const resolved = await this.resolveRole(roleName);

      const affiliation: UserAffiliation = {
        ...affiliationBase,
        roleData: resolved.role || undefined,
        permissions: resolved.permissions,
        // Alias pour compatibilité avec l'ancien code
        role: roleName as 'owner' | 'admin' | 'member' | 'viewer',
      };

      console.log(`✅ Affiliation trouvée: ${customerId} -> ${organizationId} (${affiliation.roleName})`);
      return affiliation;

    } catch (error) {
      console.error(`❌ Erreur lors de la récupération de l'affiliation:`, error);
      throw error;
    }
  }

  /**
   * Crée une nouvelle affiliation
   */
  static async create(request: CreateUserAffiliationRequest): Promise<UserAffiliation> {
    try {
      console.log(`🔍 Création d'une affiliation: ${request.customerId} -> ${request.organizationId}`);

      // Valider que firebaseUid est fourni (requis pour le nouveau format d'ID)
      if (!request.firebaseUid) {
        throw new Error('firebaseUid est requis pour créer une affiliation');
      }

      // Générer l'ID structuré: {firebaseUid}_{organizationId}
      const affiliationId = generateAffiliationId(request.firebaseUid, request.organizationId);

      // Vérifier si l'affiliation existe déjà
      const existingDoc = await getDoc(doc(db, this.COLLECTION_NAME, affiliationId));
      if (existingDoc.exists()) {
        throw new Error('Le customer est déjà affilié à cette organisation');
      }

      // Utiliser roleName (nouveau format) ou role (ancien format pour compatibilité)
      const roleName = request.roleName || 'member';

      const affiliationData: any = {
        customerId: request.customerId, // ✅ customerId est requis (métier)
        email: request.email || undefined, // ✅ Enregistrer l'email si fourni
        organizationId: request.organizationId,
        roleName: roleName, // ✅ Nouveau format: référence au rôle
        status: request.status || 'active',
        joinedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // N'ajouter invitedBy et invitedAt que s'ils sont définis
      if (request.invitedBy) {
        affiliationData.invitedBy = request.invitedBy;
        affiliationData.invitedAt = serverTimestamp();
      }

      // Utiliser setDoc avec l'ID structuré (au lieu de addDoc)
      const docRef = doc(db, this.COLLECTION_NAME, affiliationId);
      await setDoc(docRef, affiliationData);

      // Récupérer l'affiliation créée avec les timestamps résolus
      const createdDoc = await getDoc(docRef);
      const data = createdDoc.data();

      if (!data) {
        throw new Error('Impossible de récupérer l\'affiliation créée');
      }

      const affiliationBase: UserAffiliationBase = {
        id: affiliationId,
        customerId: data.customerId,
        email: data.email,
        organizationId: data.organizationId,
        roleName: data.roleName || 'member',
        status: data.status || 'active',
        joinedAt: data.joinedAt?.toDate ? data.joinedAt.toDate() : new Date(data.joinedAt || Date.now()),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || Date.now()),
        invitedBy: data.invitedBy,
        invitedAt: data.invitedAt?.toDate ? data.invitedAt.toDate() : (data.invitedAt ? new Date(data.invitedAt) : undefined),
        lastActivityAt: data.lastActivityAt?.toDate ? data.lastActivityAt.toDate() : (data.lastActivityAt ? new Date(data.lastActivityAt) : undefined),
      };

      // Résoudre le rôle
      const resolved = await this.resolveRole(affiliationBase.roleName);

      const affiliation: UserAffiliation = {
        ...affiliationBase,
        roleData: resolved.role || undefined,
        permissions: resolved.permissions,
        // Alias pour compatibilité avec l'ancien code
        role: affiliationBase.roleName as 'owner' | 'admin' | 'member' | 'viewer',
      };

      console.log(`✅ Affiliation créée avec succès: ${affiliation.id}`);
      return affiliation;

    } catch (error) {
      console.error(`❌ Erreur lors de la création de l'affiliation:`, error);
      throw error;
    }
  }

  /**
   * Met à jour une affiliation existante
   */
  static async update(affiliationId: string, request: UpdateUserAffiliationRequest): Promise<UserAffiliation> {
    try {
      console.log(`🔍 Mise à jour de l'affiliation: ${affiliationId}`);

      const docRef = doc(db, this.COLLECTION_NAME, affiliationId);

      // Préparer les données de mise à jour (convertir role en roleName si nécessaire)
      const updateData: any = {
        updatedAt: serverTimestamp(),
      };

      if (request.roleName !== undefined) {
        updateData.roleName = request.roleName;
      }
      if (request.status !== undefined) {
        updateData.status = request.status;
      }
      if (request.lastActivityAt !== undefined) {
        updateData.lastActivityAt = request.lastActivityAt instanceof Date
          ? Timestamp.fromDate(request.lastActivityAt)
          : serverTimestamp();
      }

      await updateDoc(docRef, updateData);

      // Récupérer l'affiliation mise à jour
      const updatedDoc = await getDoc(docRef);
      if (!updatedDoc.exists()) {
        throw new Error('Affiliation non trouvée');
      }

      const data = updatedDoc.data();
      const roleName = data.roleName || data.role || 'member';

      const affiliationBase: UserAffiliationBase = {
        id: affiliationId,
        customerId: data.customerId,
        email: data.email,
        organizationId: data.organizationId,
        roleName: roleName,
        status: data.status || 'active',
        joinedAt: data.joinedAt?.toDate ? data.joinedAt.toDate() : new Date(data.joinedAt || Date.now()),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt || Date.now()),
        invitedBy: data.invitedBy,
        invitedAt: data.invitedAt?.toDate ? data.invitedAt.toDate() : (data.invitedAt ? new Date(data.invitedAt) : undefined),
        lastActivityAt: data.lastActivityAt?.toDate ? data.lastActivityAt.toDate() : (data.lastActivityAt ? new Date(data.lastActivityAt) : undefined),
      };

      // Résoudre le rôle
      const resolved = await this.resolveRole(affiliationBase.roleName);

      const affiliation: UserAffiliation = {
        ...affiliationBase,
        roleData: resolved.role || undefined,
        permissions: resolved.permissions,
        // Alias pour compatibilité avec l'ancien code
        role: affiliationBase.roleName as 'owner' | 'admin' | 'member' | 'viewer',
      };

      console.log(`✅ Affiliation mise à jour avec succès: ${affiliationId}`);
      return affiliation;

    } catch (error) {
      console.error(`❌ Erreur lors de la mise à jour de l'affiliation:`, error);
      throw error;
    }
  }

  /**
   * Supprime une affiliation
   */
  static async delete(affiliationId: string): Promise<void> {
    try {
      console.log(`🔍 Suppression de l'affiliation: ${affiliationId}`);

      const docRef = doc(db, this.COLLECTION_NAME, affiliationId);
      await deleteDoc(docRef);

      console.log(`✅ Affiliation supprimée avec succès: ${affiliationId}`);

    } catch (error) {
      console.error(`❌ Erreur lors de la suppression de l'affiliation:`, error);
      throw error;
    }
  }

  /**
   * Met à jour la dernière activité d'un customer pour une organisation
   */
  static async updateLastActivity(customerId: string, organizationId: string): Promise<void> {
    try {
      const affiliation = await this.getAffiliation(customerId, organizationId);
      if (!affiliation) {
        console.warn(`⚠️ Aucune affiliation trouvée pour mettre à jour l'activité: ${customerId} -> ${organizationId}`);
        return;
      }

      const docRef = doc(db, this.COLLECTION_NAME, affiliation.id);
      await updateDoc(docRef, {
        lastActivityAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      console.log(`✅ Dernière activité mise à jour: ${customerId} -> ${organizationId}`);

    } catch (error) {
      console.error(`❌ Erreur lors de la mise à jour de l'activité:`, error);
      throw error;
    }
  }

  /**
   * Crée plusieurs affiliations en batch (pour l'initialisation)
   */
  static async createBatch(requests: CreateUserAffiliationRequest[]): Promise<UserAffiliation[]> {
    try {
      console.log(`🔍 Création en batch de ${requests.length} affiliations`);

      const batch = writeBatch(db);
      const affiliations: UserAffiliation[] = [];

      for (const request of requests) {
        // Valider que firebaseUid est fourni
        if (!request.firebaseUid) {
          console.warn(`⚠️ Affiliation ignorée: firebaseUid manquant pour ${request.customerId} -> ${request.organizationId}`);
          continue;
        }

        // Générer l'ID structuré
        const affiliationId = generateAffiliationId(request.firebaseUid, request.organizationId);

        // Vérifier si l'affiliation existe déjà
        const existingDoc = await getDoc(doc(db, this.COLLECTION_NAME, affiliationId));
        if (existingDoc.exists()) {
          console.warn(`⚠️ Affiliation déjà existante: ${affiliationId}`);
          continue;
        }

        const roleName = request.roleName || 'member';

        const docRef = doc(db, this.COLLECTION_NAME, affiliationId);
        const batchData: any = {
          customerId: request.customerId,
          email: request.email || undefined,
          organizationId: request.organizationId,
          roleName: roleName,
          status: request.status || 'active',
          joinedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        // N'ajouter invitedBy et invitedAt que s'ils sont définis
        if (request.invitedBy) {
          batchData.invitedBy = request.invitedBy;
          batchData.invitedAt = serverTimestamp();
        }

        batch.set(docRef, batchData);

        // Résoudre le rôle pour l'objet retourné
        const resolved = await this.resolveRole(roleName);

        affiliations.push({
          id: affiliationId,
          customerId: request.customerId,
          email: request.email,
          organizationId: request.organizationId,
          roleName: roleName,
          status: request.status || 'active',
          joinedAt: new Date(),
          updatedAt: new Date(),
          invitedBy: request.invitedBy,
          roleData: resolved.role || undefined,
          permissions: resolved.permissions,
          role: roleName as 'owner' | 'admin' | 'member' | 'viewer',
        });
      }

      await batch.commit();
      console.log(`✅ ${affiliations.length} affiliations créées en batch`);
      return affiliations;

    } catch (error) {
      console.error(`❌ Erreur lors de la création en batch:`, error);
      throw error;
    }
  }

  /**
   * Vérifie si un customer a accès à une organisation
   * @deprecated Utiliser hasAccessByUid pour les utilisateurs authentifiés
   */
  static async hasAccess(customerId: string, organizationId: string): Promise<boolean> {
    try {
      const affiliation = await this.getAffiliation(customerId, organizationId);
      return affiliation !== null && affiliation.status === 'active';
    } catch (error) {
      console.error(`❌ Erreur lors de la vérification d'accès:`, error);
      return false;
    }
  }

  /**
   * Vérifie si un utilisateur a accès à une organisation (par firebaseUid)
   * Version optimisée utilisant l'ID structuré
   */
  static async hasAccessByUid(firebaseUid: string, organizationId: string): Promise<boolean> {
    try {
      const affiliation = await this.getAffiliationByFirebaseUid(firebaseUid, organizationId);
      return affiliation !== null && affiliation.status === 'active';
    } catch (error) {
      console.error(`❌ Erreur lors de la vérification d'accès par UID:`, error);
      return false;
    }
  }

  /**
   * Vérifie si un customer a une permission spécifique pour une organisation
   * @deprecated Utiliser hasPermissionByUid pour les utilisateurs authentifiés
   */
  static async hasPermission(
    customerId: string,
    organizationId: string,
    permission: keyof import('@/models/role').RolePermissions
  ): Promise<boolean> {
    try {
      const affiliation = await this.getAffiliation(customerId, organizationId);
      return affiliation !== null &&
        affiliation.status === 'active' &&
        affiliation.permissions !== undefined &&
        affiliation.permissions[permission] === true;
    } catch (error) {
      console.error(`❌ Erreur lors de la vérification de permission:`, error);
      return false;
    }
  }

  /**
   * Vérifie si un utilisateur a une permission spécifique (par firebaseUid)
   * Version optimisée utilisant l'ID structuré
   */
  static async hasPermissionByUid(
    firebaseUid: string,
    organizationId: string,
    permission: keyof import('@/models/role').RolePermissions
  ): Promise<boolean> {
    try {
      const affiliation = await this.getAffiliationByFirebaseUid(firebaseUid, organizationId);
      return affiliation !== null &&
        affiliation.status === 'active' &&
        affiliation.permissions !== undefined &&
        affiliation.permissions[permission] === true;
    } catch (error) {
      console.error(`❌ Erreur lors de la vérification de permission par UID:`, error);
      return false;
    }
  }
}








